import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Input } from "@moodstudios/farmacenter-design";
import { useLista } from "../../api/useLista";

export interface SelectorBuscadoProps<T> {
  label: string;
  /** Oculta la etiqueta (en rejillas, la cabecera de columna ya rotula el campo). */
  ocultarEtiqueta?: boolean | undefined;
  // Las opcionales admiten `undefined` explícito: se reenvían desde envoltorios
  // (SelectorTercero/Producto) cuyas props opcionales pueden llegar sin definir, y
  // el proyecto usa `exactOptionalPropertyTypes`.
  placeholder?: string | undefined;
  /** Clave base de caché; se le añade el texto buscado para cachear cada término. */
  clave: readonly unknown[];
  /** Si falta el contexto necesario (p. ej. no hay tenant), no se busca. */
  habilitada?: boolean | undefined;
  /** La llamada `api.GET(...)` tipada por la ruta, parametrizada por el texto. */
  buscar: (texto: string) => Promise<{ data?: T[]; error?: unknown; response: Response }>;
  getKey: (item: T) => string;
  getLabel: (item: T) => string;
  getDetalle?: ((item: T) => ReactNode) | undefined;
  /** Se llama con el elegido, o `null` cuando el usuario reescribe e invalida la elección. */
  onSelect: (item: T | null) => void;
  minChars?: number | undefined;
  autoFocus?: boolean | undefined;
  valorInicial?: string | undefined;
  /** Texto cuando el nivel padre aún no está listo (deshabilita el campo). */
  esperando?: string | undefined;
}

/**
 * Un campo de escribir-y-buscar contra un endpoint de búsqueda del ERP. A
 * diferencia de `SelectorRemoto` (un `Select` alimentado por una lista entera),
 * éste consulta el backend a cada pocas pulsaciones y muestra las coincidencias
 * en un desplegable navegable por teclado. Es la base de los selectores de
 * tercero y producto de la captura de documentos, al estilo Dominium.
 *
 * Estado honesto: distingue "escribe más", "buscando", "sin resultados" y error;
 * nunca muestra una lista vacía como si fuera un resultado.
 */
export function SelectorBuscado<T>({
  label,
  ocultarEtiqueta,
  placeholder,
  clave,
  habilitada,
  buscar,
  getKey,
  getLabel,
  getDetalle,
  onSelect,
  minChars,
  autoFocus,
  valorInicial,
  esperando,
}: SelectorBuscadoProps<T>) {
  const minimo = minChars ?? 2;
  const [texto, setTexto] = useState(valorInicial ?? "");
  const [debounced, setDebounced] = useState("");
  const [abierto, setAbierto] = useState(false);
  const [activo, setActivo] = useState(0);
  const contenedor = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [posicion, setPosicion] = useState<{ left: number; top: number; width: number } | null>(null);
  const listId = useId();

  useEffect(() => {
    const t = setTimeout(() => setDebounced(texto.trim()), 250);
    return () => clearTimeout(t);
  }, [texto]);

  // El menú se pinta en un portal con posición fija anclada al input: así escapa del recorte
  // de cualquier contenedor con overflow (p. ej. la rejilla de líneas) y de los contextos de
  // apilamiento, en vez de quedar cortado o por debajo del contenido de la página.
  const medir = useCallback(() => {
    const el = inputRef.current;
    if (!el) {
      return;
    }
    const r = el.getBoundingClientRect();
    setPosicion({ left: r.left, top: r.bottom + 4, width: r.width });
  }, []);

  const deshabilitado = esperando !== undefined;
  const puedeBuscar = !deshabilitado && (habilitada ?? true) && debounced.length >= minimo;
  const query = useLista<T>([...clave, debounced], puedeBuscar, () => buscar(debounced));
  const resultados = query.data ?? [];

  useEffect(() => setActivo(0), [debounced]);

  useEffect(() => {
    function fuera(e: MouseEvent) {
      const objetivo = e.target as Node;
      // El menú vive en un portal fuera del contenedor: hay que exceptuarlo, si no un clic en
      // una opción se leería como "clic fuera" y cerraría antes de elegir.
      if (contenedor.current?.contains(objetivo) || menuRef.current?.contains(objetivo)) {
        return;
      }
      setAbierto(false);
    }
    document.addEventListener("mousedown", fuera);
    return () => document.removeEventListener("mousedown", fuera);
  }, []);

  function elegir(item: T) {
    onSelect(item);
    setTexto(getLabel(item));
    setAbierto(false);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!abierto && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      setAbierto(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActivo((i) => Math.min(i + 1, resultados.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActivo((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      if (abierto && resultados[activo]) {
        e.preventDefault();
        elegir(resultados[activo]);
      }
    } else if (e.key === "Escape") {
      setAbierto(false);
    }
  }

  const mostrarPanel = abierto && !deshabilitado;

  // Con el menú abierto, medir su posición y seguir el input si la página o un contenedor
  // scrollea, o si cambia el tamaño de la ventana.
  useLayoutEffect(() => {
    if (!mostrarPanel) {
      setPosicion(null);
      return;
    }
    medir();
    window.addEventListener("scroll", medir, true);
    window.addEventListener("resize", medir);
    return () => {
      window.removeEventListener("scroll", medir, true);
      window.removeEventListener("resize", medir);
    };
  }, [mostrarPanel, medir]);

  return (
    <div ref={contenedor} className="relative flex flex-col gap-1">
      {ocultarEtiqueta ? null : (
        <label className="text-sm font-medium" style={{ color: "var(--fc-color-text)" }}>
          {label}
        </label>
      )}
      <Input
        ref={inputRef}
        value={texto}
        placeholder={deshabilitado ? esperando : (placeholder ?? `Buscar ${label.toLowerCase()}…`)}
        disabled={deshabilitado}
        autoFocus={autoFocus}
        aria-label={label}
        role="combobox"
        aria-expanded={mostrarPanel}
        aria-controls={listId}
        aria-autocomplete="list"
        onChange={(e) => {
          setTexto(e.target.value);
          setAbierto(true);
          onSelect(null);
        }}
        onFocus={() => setAbierto(true)}
        onKeyDown={onKeyDown}
      />
      {mostrarPanel &&
        posicion &&
        createPortal(
          <div
            ref={menuRef}
            id={listId}
            role="listbox"
            className="fixed z-[100] max-h-64 overflow-auto rounded-md border shadow-lg"
            style={{
              left: posicion.left,
              top: posicion.top,
              width: posicion.width,
              background: "var(--fc-color-surface-raised)",
              borderColor: "var(--fc-color-border)",
            }}
          >
          {debounced.length < minimo ? (
            <Pista>Escribe al menos {minimo} caracteres.</Pista>
          ) : query.isFetching && resultados.length === 0 ? (
            <Pista>Buscando…</Pista>
          ) : query.isError ? (
            <Pista tono="var(--fc-color-danger)">No se pudo buscar. Reintenta.</Pista>
          ) : resultados.length === 0 ? (
            <Pista>Sin resultados para «{debounced}».</Pista>
          ) : (
            resultados.map((item, i) => (
              <button
                key={getKey(item)}
                type="button"
                role="option"
                aria-selected={i === activo}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setActivo(i)}
                onClick={() => elegir(item)}
                className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left"
                style={{
                  background: i === activo ? "var(--fc-color-action)" : "transparent",
                  color: i === activo ? "var(--fc-color-on-action)" : "var(--fc-color-text)",
                }}
              >
                <span className="text-sm font-medium">{getLabel(item)}</span>
                {getDetalle && (
                  <span
                    className="text-xs"
                    style={{
                      color: i === activo
                        ? "var(--fc-color-on-action)"
                        : "var(--fc-color-text-muted)",
                    }}
                  >
                    {getDetalle(item)}
                  </span>
                )}
              </button>
            ))
          )}
          </div>,
          document.getElementById("root") ?? document.body,
        )}
    </div>
  );
}

function Pista({ children, tono }: { children: ReactNode; tono?: string }) {
  return (
    <p className="px-3 py-2 text-xs" style={{ color: tono ?? "var(--fc-color-text-muted)" }}>
      {children}
    </p>
  );
}
